import { useEffect, useState } from 'react';
import { requestCallSummary, type CallSummary } from './api';

/**
 * The AI summary of a finished call for the disposition dialogs (dialer 10 and the record's E1, docs/dev/api-dialer.md
 * „AI\"): requested once per call id; `none` when there is no transcript, no AI or the request failed — the dialog then
 * stays as it is without a suggestion. Nothing is ever submitted from here.
 */

export type CallSummaryState =
  { status: 'idle' } | { status: 'loading' } | { status: 'ready'; summary: CallSummary } | { status: 'none' };

export const SUMMARY_LOADING_TEXT = 'KI-Zusammenfassung wird erstellt…';
export const SUMMARY_SUGGESTION_TEXT =
  'Vorschlag der KI-Zusammenfassung — Ergebnis und Notiz sind frei änderbar.';

export function useCallSummary(callId: string | null): CallSummaryState {
  const [result, setResult] = useState<{ callId: string; state: CallSummaryState } | null>(null);
  useEffect(() => {
    if (callId === null) return;
    let cancelled = false;
    requestCallSummary(callId)
      .then((summary) => {
        if (cancelled) return;
        const usable = summary.summary !== null || summary.suggestedDisposition !== null;
        setResult({ callId, state: usable ? { status: 'ready', summary } : { status: 'none' } });
      })
      .catch(() => {
        if (!cancelled) setResult({ callId, state: { status: 'none' } });
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);
  if (callId === null) return { status: 'idle' };
  return result?.callId === callId ? result.state : { status: 'loading' };
}

/** The note with the summary: the summary alone, or below the setter's own lines. */
export function noteWithSummary(note: string, summary: string | null): string {
  if (summary === null || summary.trim() === '') return note;
  const own = note.trim();
  if (own === '') return summary;
  if (own.includes(summary.trim())) return note;
  return `${own}\n\n${summary}`;
}
